import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, DollarSign, ArrowRight, RefreshCw } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Badge } from './ui/badge';

const STATUS_COLORS = {
  lead: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  sale: 'bg-green-500/15 text-green-400 border-green-500/30',
  reject: 'bg-red-500/15 text-red-400 border-red-500/30',
  upsell: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  rebill: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  chargeback: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export default function ConversionTimeline({ campaignId }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/conversions`, {
        params: { page, limit: 50 },
      });
      setData(res.data);
    } catch {
      toast.error(t('Failed to load conversions'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (campaignId) fetchData();
  }, [campaignId, page]);

  if (loading && !data) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="skeleton h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  const items = data?.items || [];
  const total = data?.total || 0;
  const pageSize = data?.page_size || 50;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4" data-testid="conversion-timeline">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('{count} conversions', { count: total })}
        </p>
        <button type="button" className="xp-btn" onClick={fetchData}>
          <RefreshCw style={{ width: 14, height: 14 }} /> {t('Refresh')}
        </button>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>{t('No conversions yet')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((conv) => (
            <Card key={conv.id} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={STATUS_COLORS[conv.status] || 'bg-muted text-muted-foreground'}>
                        {conv.status}
                      </Badge>
                      <span className="text-xs font-mono text-muted-foreground truncate max-w-[200px]">
                        {conv.click_id}
                      </span>
                    </div>

                    {/* Status timeline */}
                    {conv.status_history && conv.status_history.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap text-xs">
                        {conv.status_history.map((h, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />}
                            <span
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${STATUS_COLORS[h.status] || 'bg-muted/50 text-muted-foreground border-border'}`}
                            >
                              {h.status}
                              {h.revenue > 0 && <span className="font-mono">${h.revenue}</span>}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="text-right shrink-0 space-y-1">
                    <p className="tabular-nums font-semibold">
                      ${(conv.revenue || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                      <Clock className="w-3 h-3" />
                      {fmtDate(conv.timestamp)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 pt-2">
          <button
            type="button"
            className="xp-btn"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            {t('Previous')}
          </button>
          <span className="text-sm text-muted-foreground self-center">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className="xp-btn"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            {t('Next')}
          </button>
        </div>
      )}
    </div>
  );
}
